import { ClientResourceGroupFragment } from "@graphql/client-resource-groups/ClientResourceGroups.g";

import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { Box } from "@design-system/Box/Box";
import { Text } from "@design-system/Text/Text";
import { Tag } from "@design-system/Tag/Tag";

type Props = {
  resourceGroup: ClientResourceGroupFragment | null;
  isOpen: boolean;
  onClose: () => void;
};

export const ClientResourceInfoPanel = ({
  resourceGroup,
  isOpen,
  onClose,
}: Props) => {
  if (!resourceGroup || !resourceGroup.primaryResource) {
    return null;
  }

  const resource = resourceGroup.primaryResource;

  return (
    <SlideInPanel
      isOpen={isOpen}
      onClose={onClose}
      title={`Resource Info: ${resource.productName || "Unknown"}`}
      applyButtonText=""
    >
      <Box direction="column" gap={24}>
        <Box direction="column" gap={16}>
          <Text size={16} weight={500}>
            Product Details
          </Text>
          <Box direction="column" gap={8}>
            <Box>
              <Text size={11} color="textSecondary">Product Name</Text>
              <Text>{resource.productName || "—"}</Text>
            </Box>
            <Box>
              <Text size={11} color="textSecondary">Vendor Name</Text>
              <Text>{resource.vendorName || "—"}</Text>
            </Box>
            {resource.productDescription && (
              <Box>
                <Text size={11} color="textSecondary">Product Description</Text>
                <Text>{resource.productDescription}</Text>
              </Box>
            )}
            {resource.vendorDescription && (
              <Box>
                <Text size={11} color="textSecondary">Vendor Description</Text>
                <Text>{resource.vendorDescription}</Text>
              </Box>
            )}
          </Box>
        </Box>

        <hr style={{
          border: "none",
          borderTop: "1px solid var(--color-border-subtle)",
          margin: 0
        }} />

        <Box direction="column" gap={16}>
          <Text size={16} weight={500}>
            Resource Properties
          </Text>
          <Box direction="column" gap={12}>
            <Box align="center" gap={8}>
              <div style={{ minWidth: "120px" }}>
                <Text size={11} color="textSecondary">
                  Resource Type:
                </Text>
              </div>
              {resource.isSaas ? (
                <Tag text="SaaS" color="success" />
              ) : (
                <Tag text="Non-SaaS" color="tertiary" />
              )}
            </Box>
            <Box align="center" gap={8}>
              <div style={{ minWidth: "120px" }}>
                <Text size={11} color="textSecondary">
                  Visibility Status:
                </Text>
              </div>
              {resource.isVisible ? (
                <Tag text="Visible" color="success" />
              ) : (
                <Tag text="Hidden" color="warning" />
              )}
            </Box>
            <Box align="center" gap={8}>
              <div style={{ minWidth: "120px" }}>
                <Text size={11} color="textSecondary">
                  Consumer App:
                </Text>
              </div>
              {resource.isForConsumers ? (
                <Tag text="Yes" color="primary" />
              ) : (
                <Tag text="No" color="tertiary" />
              )}
            </Box>
          </Box>
          <Box direction="column" gap={8}>
            <div style={{
              padding: "12px",
              backgroundColor: "var(--color-bg-secondary)",
              borderRadius: "8px"
            }}>
              <Text size={11} color="textSecondary">
                <i>Note: Resource visibility settings are managed centrally by administrators and apply to all clients.</i>
              </Text>
            </div>
          </Box>
        </Box>

        {resource.publicResource?.category && (
          <>
            <hr style={{
              border: "none",
              borderTop: "1px solid var(--color-border-subtle)",
              margin: 0
            }} />
            <Box direction="column" gap={16}>
              <Text size={16} weight={500}>
                Category
              </Text>
              <Box direction="column" gap={8}>
                {resource.publicResource.category.parent && (
                  <Box>
                    <Text size={11} color="textSecondary">Parent Category</Text>
                    <Text>{resource.publicResource.category.parent.name}</Text>
                  </Box>
                )}
                <Box>
                  <Text size={11} color="textSecondary">Category</Text>
                  <Text>{resource.publicResource.category.name}</Text>
                </Box>
              </Box>
            </Box>
          </>
        )}

        {/* TODO: Re-enable when usage statistics are available in schema
        <hr style={{
          border: "none",
          borderTop: "1px solid var(--color-border-subtle)",
          margin: 0
        }} />
        <Box direction="column" gap={16}>
          <Text size={16} weight={500}>
            Usage Statistics
          </Text>
          <Box direction="column" gap={8}>
            <Text size={11} color="textSecondary">Statistics will be available soon</Text>
          </Box>
        </Box>
        */}
      </Box>
    </SlideInPanel>
  );
};